import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Blocker,
  BlockerFunction,
  NavigationType,
  useBlocker,
  useLocation,
  useNavigate,
  useNavigationType
} from 'react-router-dom';
import useAnalyticsContext from '|/hooks/use-analytics-context';

type BlockerEntry = {
  shouldBlockFn: BlockerFunction;
  handler: (blocker: Blocker) => void;
  managed: boolean;
};

type NavDirection = 'back' | 'forward';

// Passed to a managed handler when we close it in response to a real POP (sentinel mode), where
// there is no react-router blocker to resolve — the handler only needs to run its onClose.
const NOOP_BLOCKER = {
  state: 'unblocked',
  proceed: undefined,
  reset: undefined,
  location: undefined
} as unknown as Blocker;

// PERMANENT instrumentation (TXCC-9643) — fires on every browser history POP (Back OR Forward, with
// a `direction` field) in both experiment buckets. NOT experiment-scoped: exclude from any future
// pp-backstop cleanup.
const BROWSER_NAV_EVENT = 'pp_browser_nav_button';

// Marks the sentinel history entry. It must be a query param rather than a hash: checkout strips
// the hash with its own replaceState (use-checkout-vm.ts), which would silently erase the marker.
// react-router-dom 6.24.1 — a same-URL sentinel is indistinguishable from the real entry to the
// router, which is what made Back a no-op once two adjacent entries shared a URL.
const SENTINEL_PARAM = 'sentinel';

const searchHasSentinel = (search: string) => new URLSearchParams(search).has(SENTINEL_PARAM);

const isOnSentinelEntry = () => searchHasSentinel(window.location.search);

// Identifies the page a location points at, ignoring the sentinel. A sentinel push is an internal
// history artifact, not a page change, but it still mints a fresh location.key — so anything that
// reacts to "the user navigated" must compare this rather than the key, or opening a managed modal
// looks like a navigation and immediately undoes itself.
const toNavIdentity = ({ pathname, search }: { pathname: string; search: string }) => {
  const params = new URLSearchParams(search);
  params.delete(SENTINEL_PARAM);
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}`;
};

type NavigationBlockContextValue = {
  addBlocker: (
    key: string,
    shouldBlockFn: BlockerFunction,
    handler: (blocker: Blocker) => void,
    options?: { managed?: boolean }
  ) => void;
  removeBlocker: (key: string) => void;
  addOpenModal: (key: string) => void;
  removeOpenModal: (key: string) => void;
};

const NavigationBlockContext = createContext<NavigationBlockContextValue>({
  addBlocker: () => {},
  removeBlocker: () => {},
  addOpenModal: () => {},
  removeOpenModal: () => {}
});

const NavigationBlockContextProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const blockersRef = useRef<Map<string, BlockerEntry>>(new Map());
  const capturedKeysRef = useRef<string[]>([]);
  const [blockerCount, setBlockerCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigationType = useNavigationType();

  const { trackEvent } = useAnalyticsContext();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;

  // Browser history POP direction (Back vs Forward) is derived from the react-router history index
  // (history.state.idx) delta — a POP to a lower index is Back, to a higher index is Forward. The
  // popstate event itself carries no direction. `prevIdxRef` tracks the currently-committed index.
  const readIdx = () => (window.history.state as { idx?: number } | null)?.idx;
  const prevIdxRef = useRef<number>(readIdx() ?? 0);
  // Direction of the in-flight POP, captured in the blocker predicate: a blocked POP never commits a
  // location change, so the target index is only visible at predicate time (before react-router
  // restores the URL). Defaults to 'back' — the guard exists to intercept Back.
  const popDirectionRef = useRef<NavDirection>('back');
  const readPopDirection = useCallback((): NavDirection => {
    const target = readIdx();
    return target !== undefined && target > prevIdxRef.current ? 'forward' : 'back';
  }, []);

  // Modal open-presence registry (PERMANENT) — ordered by open sequence so the top-most (most
  // recently opened) modal is the last entry. Feeds pp_browser_nav_button's modalOpen/modalName and
  // is independent of addBlocker (a modal registers presence even when it arms no back-guard).
  const openModalsRef = useRef<string[]>([]);

  const addOpenModal = useCallback((key: string) => {
    if (!openModalsRef.current.includes(key)) openModalsRef.current.push(key);
  }, []);

  const removeOpenModal = useCallback((key: string) => {
    openModalsRef.current = openModalsRef.current.filter(k => k !== key);
  }, []);

  // Presence as of the moment the user pressed Back/Forward. A POP that actually navigates (control,
  // where no managed guard intercepts it) unmounts the open modal in the SAME commit as the location
  // change, and React runs every effect cleanup before any effect — so the registry is already empty
  // by the time the completed-POP effect emits. Snapshotting in the blocker predicate is what makes
  // the reading pre-commit: react-router-dom 6.24.1 (@remix-run/router 1.17.1) calls it from its
  // history listener before startNavigation (router.cjs.js — history.listen → shouldBlockNavigation).
  // Only a committed POP consumes it; see the completed-POP effect.
  const popModalsRef = useRef<string[] | null>(null);

  const emitBrowserNav = useCallback(
    (from: string, to: string, block: boolean, direction: NavDirection, names: string[]) => {
      const modalName = names.length === 0 ? undefined : names.length === 1 ? names[0] : [...names];
      trackEventRef.current(BROWSER_NAV_EVENT, {
        from,
        to,
        modalOpen: names.length > 0,
        modalName,
        block,
        direction
      });
    },
    []
  );

  // Managed-modal back-close runs entirely on a sentinel history entry, never on react-router's
  // useBlocker POP interception — that path is unreliable (it can swallow the first POP after a
  // blocker arms, and it cannot tell two adjacent entries apart when they share a URL). The first
  // managed modal to open pushes ONE sentinel entry; later modals just join the set. A single Back
  // pops that sentinel and closes the WHOLE stack, so one sentinel covers the entire modal set and
  // never needs re-arming — a Back always leaves the user with no modals open.
  const sentinelActiveRef = useRef(false);
  const countManaged = useCallback(
    () => Array.from(blockersRef.current.values()).filter(entry => entry.managed).length,
    []
  );

  // Back clears the whole modal set, and nothing may survive it. Enforced here rather than asking
  // each consumer to cooperate: a managed modal's back-close runs the same onClose the ✕ runs, so a
  // parent that re-opens itself when its child closes (the bank-account disclosure behind
  // Plaid/manual entry, which predates this work) cannot tell the two apart and puts itself straight
  // back on screen. Any managed modal that registers inside this window is closed immediately.
  //
  // It also has to hold for a second reason. Chrome's history-manipulation intervention flags a
  // pushState with no user activation behind it and lets Back skip the entire run of flagged entries
  // — ejecting the user from the funnel instead of closing a modal. A teardown re-render has no
  // gesture anywhere in its task, so arming a sentinel there mints exactly such an entry.
  //
  // The window is "since the back-close, with no user interaction" — so only effect-driven re-opens
  // land in it. A modal the user actually opened arrives after a pointerdown/keydown and is
  // untouched.
  const backCloseUnwindingRef = useRef(false);
  useEffect(() => {
    const onInteraction = () => {
      backCloseUnwindingRef.current = false;
    };
    window.addEventListener('pointerdown', onInteraction, true);
    window.addEventListener('keydown', onInteraction, true);
    return () => {
      window.removeEventListener('pointerdown', onInteraction, true);
      window.removeEventListener('keydown', onInteraction, true);
    };
  }, []);
  // Suppresses the completed-POP emit for a programmatic POP that isn't a user nav — the sentinel
  // popstate (already emitted from onPop) and removeBlocker's navigate(-1) cleanup.
  const suppressNextPopEmitRef = useRef(false);

  // The router runs under a basename ('/purchase'), so sentinel paths are built from react-router's
  // location, not window.location.
  const buildPath = useCallback((withSentinel: boolean) => {
    const loc = locationRef.current;
    const params = new URLSearchParams(loc.search);
    if (withSentinel) params.set(SENTINEL_PARAM, 'true');
    else params.delete(SENTINEL_PARAM);
    const query = params.toString();
    return `${loc.pathname}${query ? `?${query}` : ''}${loc.hash}`;
  }, []);

  const pushSentinel = useCallback(() => {
    // A reload (or restored tab) can land us on a URL that still carries the marker while no guard
    // is armed. Pushing on top of it would make the sentinel and the real entry share a URL again —
    // the exact ambiguity this marker exists to prevent — so strip the stale one first.
    if (isOnSentinelEntry()) navigate(buildPath(false), { replace: true });
    sentinelActiveRef.current = true;
    navigate(buildPath(true), { state: locationRef.current.state });
  }, [buildPath, navigate]);

  const blocker = useBlocker(
    useCallback(
      args => {
        // Capture the POP direction here: a blocked POP never commits, so the target index is only
        // readable now, before react-router restores the URL. (react-router skips the blocker on its
        // restore go(), so this isn't overwritten by the restore's popstate.)
        if (args.historyAction === NavigationType.Pop) {
          popDirectionRef.current = readPopDirection();
          popModalsRef.current = [...openModalsRef.current];
        }
        const matched: string[] = [];
        blockersRef.current.forEach((entry, key) => {
          if (!entry.shouldBlockFn(args)) return;
          // Managed modals resolve through the sentinel popstate path below — never block here.
          if (entry.managed) return;
          matched.push(key);
        });
        capturedKeysRef.current = matched;
        return matched.length > 0;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [blockerCount]
    )
  );

  useEffect(() => {
    const onPop = () => {
      // removeBlocker clears the flag before its own navigate(-1), so that cleanup POP no-ops here.
      if (!sentinelActiveRef.current) return;
      // Still sitting on the sentinel — this POP isn't the Back we're guarding against.
      if (isOnSentinelEntry()) return;

      const managed = Array.from(blockersRef.current.values()).filter(entry => entry.managed);
      if (managed.length === 0) {
        sentinelActiveRef.current = false;
        return;
      }

      // Deliberately does NOT emit pp_browser_nav_button. react-router registers its own popstate
      // listener when the router is created — before this one — and React flushes the resulting
      // location commit inside that dispatch, so the completed-POP effect has already run by the
      // time we get here. Any flag set now is too late to suppress it. That effect owns the emit and
      // recognises this case on its own (sentinel param present before the POP, absent after).

      // Back consumed the sentinel and closes the whole stack, so nothing is left to guard. Cleared
      // BEFORE the handlers run: closing releases each guard, and removeBlocker must not read a
      // still-active sentinel and try to consume it again with navigate(-1).
      sentinelActiveRef.current = false;

      // Set before the handlers run, so anything that re-opens in the commit their onClose schedules
      // lands inside the window. Only Back sets it — a manual close leaves it false.
      backCloseUnwindingRef.current = true;

      // Top-most first (Map preserves insertion order, so reverse is newest-first) — nested modals
      // tear down before the parent that renders them.
      [...managed].reverse().forEach(entry => {
        entry.handler(NOOP_BLOCKER);
      });
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    const keys = capturedKeysRef.current;
    capturedKeysRef.current = [];

    const here = locationRef.current.pathname;
    // A blocked navigation never commits, so nothing unmounted and the live registry is accurate.
    // The predicate's snapshot is deliberately NOT used here: it is only written for a POP, and an
    // unmanaged blocker (verifx) also blocks PUSHes — reading it would report a previous Back's
    // modals on a blocked PUSH.
    emitBrowserNav(
      here,
      blocker.location?.pathname ?? here,
      true,
      popDirectionRef.current,
      openModalsRef.current
    );

    // proceed() re-issues the navigation we just reported, and react-router skips the predicate
    // while a blocker is 'proceeding' (router.cjs.js — shouldBlockNavigation), so that POP commits
    // and reaches the completed-POP effect. Without this mark one Back press emits twice, the second
    // time reading a registry the commit has already emptied. The blocked emit above is the one that
    // survives: it carries the modal state as it was when the user pressed Back.
    const guarded = {
      ...blocker,
      proceed:
        blocker.proceed &&
        (() => {
          suppressNextPopEmitRef.current = true;
          blocker.proceed?.();
        })
    } as Blocker;

    // Only unmanaged blockers (e.g. the verifx MFE) reach here — the predicate skips managed modals,
    // which resolve on the sentinel popstate instead. Each unmanaged blocker owns its resolution.
    keys.forEach(key => blockersRef.current.get(key)?.handler(guarded));
  }, [blocker, emitBrowserNav]);

  // Completed-POP path — a browser Back/Forward that actually navigated (no managed guard closed a
  // modal). A blocked POP leaves the location unchanged, so this effect does not fire for it — the
  // one exception is a blocker that proceeds, which the wrapped proceed() above suppresses.
  // Skips the initial mount (react-router reports POP on load).
  const didMountRef = useRef(false);
  const prevLocationRef = useRef(location);
  useEffect(() => {
    const prev = prevLocationRef.current;
    prevLocationRef.current = location;
    const prevIdx = prevIdxRef.current;
    const curIdx = readIdx();
    if (curIdx !== undefined) prevIdxRef.current = curIdx;
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    // Only a committed location change counts. react-router hands back the same location object
    // across re-renders, so this collapses any repeat run of this effect into the single emit its
    // navigation deserves.
    if (prev === location) return;
    // This navigation committed, so the predicate's snapshot belongs to it — consumed here rather
    // than at emit time so the non-emitting returns below cannot strand it for a later navigation
    // to report. removeBlocker's navigate(-1) is a real POP and takes that path.
    const popModals = popModalsRef.current;
    popModalsRef.current = null;
    // Our own navigate(-1) cleanup (a manual close consuming the sentinel) is not a user nav. This
    // flag is set synchronously before that navigate, so unlike the popstate listener it is always
    // in place before this effect runs.
    if (suppressNextPopEmitRef.current) {
      suppressNextPopEmitRef.current = false;
      return;
    }
    if (navigationType !== NavigationType.Pop) return;
    const direction: NavDirection = curIdx !== undefined && curIdx > prevIdx ? 'forward' : 'back';
    // Leaving a sentinel entry IS the managed back-close: this POP closed a modal rather than
    // navigating the user anywhere, which is what `block` reports.
    const closedAModal = searchHasSentinel(prev.search) && !searchHasSentinel(location.search);
    emitBrowserNav(
      prev.pathname,
      location.pathname,
      closedAModal,
      direction,
      popModals ?? openModalsRef.current
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigationType, emitBrowserNav]);

  // The sentinel's lifecycle is driven by the managed blockers themselves rather than a parallel
  // counter: the registry is already keyed and idempotent, so the two can never drift.
  const addBlocker = useCallback(
    (
      key: string,
      shouldBlockFn: BlockerFunction,
      handler: (blocker: Blocker) => void,
      options?: { managed?: boolean }
    ) => {
      const managed = options?.managed ?? false;
      const isNewKey = !blockersRef.current.has(key);
      const hadManaged = countManaged() > 0;

      blockersRef.current.set(key, { shouldBlockFn, handler, managed });
      if (isNewKey) {
        setBlockerCount(c => c + 1);
      }

      if (!managed) return;

      // Opening inside the back-close window means an effect re-opened this modal on the very press
      // meant to clear the set. Close it straight back and arm nothing — no sentinel is minted, so
      // there is no gesture-less history entry either.
      if (backCloseUnwindingRef.current) {
        handler(NOOP_BLOCKER);
        return;
      }

      if (!hadManaged && !sentinelActiveRef.current) pushSentinel();
    },
    [countManaged, pushSentinel]
  );

  const removeBlocker = useCallback(
    (key: string) => {
      const entry = blockersRef.current.get(key);
      if (!entry) return;
      blockersRef.current.delete(key);
      setBlockerCount(c => c - 1);

      if (!entry.managed) return;

      // Deferred because a modal→modal handoff (autopay's disclosure closing as it opens Plaid)
      // passes through zero managed blockers: both setStates land in ONE commit, and React runs
      // every effect cleanup before any effect, so the replacement registers only after this
      // cleanup. Consuming synchronously here read that gap as "the last modal closed" and fired a
      // navigate(-1) that the replacement's own sentinel then absorbed as a user Back, closing the
      // modal the instant it opened. A microtask lets the same commit finish registering first.
      queueMicrotask(() => {
        if (countManaged() > 0) return;

        // Last managed modal really did close. Consume the sentinel only if a manual (non-Back)
        // close left us sitting on it: if Back already popped it, onPop cleared the flag; if the
        // app navigated on, the URL marker is gone. In either case navigate(-1) would eat a real
        // entry. Reading the LIVE URL avoids useLocation's one-render lag on a fast open→close.
        const shouldConsume = sentinelActiveRef.current && isOnSentinelEntry();
        // Clear first so the navigate(-1) popstate below doesn't re-trigger the close listener.
        sentinelActiveRef.current = false;
        if (shouldConsume) {
          // Programmatic cleanup POP, not a user nav — don't emit pp_browser_nav_button for it.
          suppressNextPopEmitRef.current = true;
          navigate(-1);
        }
      });
    },
    [countManaged, navigate]
  );

  return (
    <NavigationBlockContext.Provider
      value={{ addBlocker, removeBlocker, addOpenModal, removeOpenModal }}
    >
      {children}
    </NavigationBlockContext.Provider>
  );
};

const useNavigationBlockContext = () => useContext(NavigationBlockContext);

export { NavigationBlockContextProvider, useNavigationBlockContext, toNavIdentity };
export type { NavigationBlockContextValue };
